import { useEffect, useState } from 'react';

import { useErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { PrimaryButton } from '@pulse-web-ui/button-v2';
import { observer } from 'mobx-react-lite';

import { sendAnalyticEvent } from '@app/web-analytic';

import {
  type Address,
  type UpdateProfileDraftRequestParams,
  profileSectionToAnalyticEvent,
  useUpdateProfileDraft,
} from '@entities/index';

import {
  AddressType,
  AnalyticEvent,
  BoundaryErrorType,
  PROFILE,
  ProfileSection,
  addTestAttribute,
  getMismatches,
  sendRequestErrorsAnalytics,
  useStores,
} from '@shared/index';

export interface SubmitAddressDraftProps {
  isSubmitDisabled: boolean;
  registrationAddress?: Address;
  actualAddress?: Address;
  onSubmit: (cb: () => void) => void;
  isFormDirty: boolean;
}

export const SubmitAddressDraft = observer(
  ({
    onSubmit,
    registrationAddress,
    actualAddress,
    isSubmitDisabled,
  }: SubmitAddressDraftProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { showBoundary } = useErrorBoundary();
    const {
      MainStore: {
        agentContractStore: { agentProfileDraft, updateAgentProfileDraft },
      },
    } = useStores();

    const [requestParams, setRequestParams] =
      useState<UpdateProfileDraftRequestParams>({
        agentProfileChange: {
          ...agentProfileDraft,
          addressesAgents: undefined,
        },
      });

    const {
      res,
      error,
      isFetching,
      refetch: updateProfileDraft,
    } = useUpdateProfileDraft(requestParams, [requestParams]);

    useEffect(() => {
      if (!!requestParams?.agentProfileChange?.addressesAgents?.length) {
        sendAnalyticEvent(AnalyticEvent.AGENT_EDITPERSONALDATA_SAVE, {
          category: profileSectionToAnalyticEvent[ProfileSection.ADDRESS],
        });

        updateProfileDraft();
      }
    }, [requestParams]);

    useEffect(() => {
      if (error) {
        const mismatches = getMismatches(error);
        sendRequestErrorsAnalytics(
          AnalyticEvent.AGENT_EDITPERSONALDATA_OPEN_ERROR,
          mismatches
        );
        showBoundary(BoundaryErrorType.SomethingWentWrong);
      }
    }, [error]);

    useEffect(() => {
      if (
        !isFetching &&
        res &&
        !!requestParams.agentProfileChange.addressesAgents?.length
      ) {
        updateAgentProfileDraft(requestParams.agentProfileChange);
        navigate(PROFILE);
      }
    }, [requestParams, isFetching, res]);

    const createRequestPayload = () => {
      if (registrationAddress) {
        const realAddress = actualAddress ?? {
          ...registrationAddress,
          addressType: AddressType.ACCOMMODATION,
        };

        setRequestParams((prevParams: UpdateProfileDraftRequestParams) => ({
          ...prevParams,
          agentProfileChange: {
            ...prevParams.agentProfileChange,
            addressesAgents: [registrationAddress, realAddress],
          },
        }));
      }
    };

    const submitPage = () => {
      onSubmit(createRequestPayload);
    };

    return (
      <PrimaryButton
        size='l'
        onClick={submitPage}
        disabled={isSubmitDisabled}
        isLoading={isFetching}
        {...addTestAttribute('agentAddressDraft.submitButton')}
      >
        {t('COMMON:buttons.save')}
      </PrimaryButton>
    );
  }
);
